import type { TaxDocument, TaxDocumentLine } from '../api/taxDocuments';
import { formatAmount } from '../format/money';
import type { StatusTone } from '../vocabulary';
import type { TaxDocumentKind, TaxDocumentLineKind } from '../generated/enums';

/**
 * The pure half of the invoice register.
 *
 * <p>
 * Naming and grouping only — <strong>there is deliberately no arithmetic here.</strong> Every figure
 * on a tax document was frozen when the supply happened, and a second implementation of GST on the
 * client could only ever disagree with a document that has been filed. That is the opposite of
 * `pricing.ts`, which mirrors the server precisely so a clerk watches the money move while they
 * type; there is nothing to watch here.
 * </p>
 */

/**
 * What a document is called on screen.
 *
 * <p>
 * <strong>An invoice is not always a "tax invoice".</strong> A shop below the registration threshold
 * issues a bill of supply, and titling that sheet "Tax invoice" would put a claim on it the shop is
 * not entitled to make. Derived from whether a GSTIN was snapshotted onto the document rather than
 * from a stored flag, so it reads back correctly for documents raised before the shop registered.
 * </p>
 */
export function documentTitle(document: {
  kind: TaxDocumentKind;
  isBillOfSupply: boolean;
}): string {
  if (document.kind === 'CreditNote') {
    return 'Credit note';
  }

  if (document.kind === 'DebitNote') {
    return 'Debit note';
  }

  return document.isBillOfSupply ? 'Bill of supply' : 'Tax invoice';
}

/**
 * A short label for the register's kind column, where the row already says whose it is.
 *
 * The bill-of-supply distinction is deliberately not made here: the column is two characters wide
 * in spirit, and the sheet itself is where the title has to be exact.
 */
const kindLabels: Record<TaxDocumentKind, string> = {
  Invoice: 'Invoice',
  CreditNote: 'Credit note',
  DebitNote: 'Debit note',
};

export function kindLabel(kind: TaxDocumentKind): string {
  return kindLabels[kind];
}

/**
 * The tone a kind carries.
 *
 * <p>
 * <strong>A credit note is `warning`, not `negative`.</strong> Red means late and a problem
 * everywhere else in this product, and a credit note is neither — it is an ordinary correction, and
 * colouring it as a failure teaches an operator to read a normal month as a bad one. It is the only
 * one of the three that runs against the shop, which is what the amber is for.
 * </p>
 */
export function kindTone(kind: TaxDocumentKind): StatusTone {
  return kind === 'CreditNote' ? 'warning' : kind === 'DebitNote' ? 'brand' : 'positive';
}

/**
 * What a line is charging for, in the words a customer would use.
 *
 * <p>
 * Two guards, the pair this codebase requires of any wording map over a generated enum:
 * `Record<TaxDocumentLineKind, string>` makes a new member a compile error, and a test asserts no
 * label is empty — because `''` satisfies the type and renders nothing at all.
 * </p>
 */
const lineKindLabels: Record<TaxDocumentLineKind, string> = {
  Rental: 'Hire',
  Alteration: 'Alteration',
  LateFee: 'Late return',
  Damage: 'Damage',
  AccessoryReplacement: 'Not returned',
  CancellationFee: 'Cancellation charge',
  Sale: 'Sale',
};

export function lineKindLabel(kind: TaxDocumentLineKind): string {
  return lineKindLabels[kind];
}

export const lineKinds = Object.keys(lineKindLabels) as TaxDocumentLineKind[];

/**
 * The muted second line under a line's description — its kind, and what came off it.
 *
 * <p>
 * <strong>The kind is dropped when it only repeats the description</strong>, which a late-fee line
 * does: its description <em>is</em> "Late return", and the same words printed underneath themselves
 * read as a rendering fault rather than as a label. Found by opening a real debit note; a rental
 * line, whose description is a garment, still wants the word "Hire" beneath it.
 * </p>
 *
 * <p>
 * <strong>The money formatter is the caller's, because the two readers format differently.</strong>
 * The register's panel is a screen and shows a bare figure; the printed sheet is a customer document
 * and shows the amount <em>and</em> its symbol, per cross-cutting rule 9. One function with the
 * formatter passed in is what stopped the sheet growing its own copy of this rule — which is exactly
 * what it had, and what printed "Late return" twice.
 * </p>
 */
export function lineNote(
  line: Pick<TaxDocumentLine, 'kind' | 'description' | 'discountAmount'>,
  money: (value: number) => string = formatAmount,
): string {
  const label = lineKindLabel(line.kind);

  const parts = [
    line.description.trim().toLowerCase() === label.toLowerCase() ? '' : label,
    line.discountAmount > 0 ? `less ${money(line.discountAmount)}` : '',
  ].filter((part) => part.length > 0);

  return parts.join(' · ');
}

/**
 * Which tax columns a document actually uses.
 *
 * <p>
 * <strong>A sheet shows CGST and SGST, or IGST — never all three with two of them blank.</strong>
 * The split is decided once, at issue, by where the supply was made; printing the columns that were
 * not charged invites a reader to wonder whether something is missing. A bill of supply shows none
 * of them, because it charged none.
 * </p>
 */
export function taxColumns(document: {
  isBillOfSupply: boolean;
  isInterState: boolean;
}): 'none' | 'igst' | 'cgst-sgst' {
  if (document.isBillOfSupply) {
    return 'none';
  }

  return document.isInterState ? 'igst' : 'cgst-sgst';
}

/**
 * The lines in the order a document prints them, hire first.
 *
 * <p>
 * By the server's own `sortOrder` rather than by kind, because that is the order the document was
 * built in and a filed sheet must not reshuffle itself between two prints. The id is the tie-break
 * for rows written before ordering existed.
 * </p>
 */
export function orderedLines(lines: readonly TaxDocumentLine[]): TaxDocumentLine[] {
  return [...lines].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

/**
 * The code a line is classified under, and what its column is headed. A hire is a service (SAC); a
 * retail sale's line is goods (HSN). A document never mixes the two, but the heading follows the
 * lines rather than the document kind so it cannot disagree with what is printed under it.
 */
export function lineCode(
  line: Pick<TaxDocumentLine, 'kind' | 'sacCode' | 'hsnCode'>,
): string | null {
  return line.kind === 'Sale' ? line.hsnCode : line.sacCode;
}

export function codeHeading(lines: readonly Pick<TaxDocumentLine, 'kind'>[]): 'HSN' | 'SAC' {
  return lines.length > 0 && lines.every((line) => line.kind === 'Sale') ? 'HSN' : 'SAC';
}

/**
 * Whether this document can be withdrawn at all, as the server will decide it.
 *
 * <p>
 * <strong>Mirrors the server rather than guessing</strong>, which is the standing rule: never offer
 * a write the API will refuse. The server also refuses an invoice on a booking whose garments have
 * gone out — which the client cannot see from the document alone, so the button stays available and
 * the refusal arrives as a sentence naming the reason. That asymmetry is deliberate: hiding the
 * action would leave a clerk with a document they were told to cancel and no way to find out why
 * they cannot.
 * </p>
 */
export function canCancel(document: Pick<TaxDocument, 'status'>): boolean {
  return document.status !== 'Cancelled';
}
